import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/Firebase'; // Adjust the import path based on your project structure
import { collection, doc, setDoc, getDocs, getDoc, query, orderBy, Timestamp, where } from 'firebase/firestore';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaUserPlus } from 'react-icons/fa';

// Move initialFormData outside the component to fix scoping/HMR issues
const initialFormData = {
  // Basic Info (ordered: Patient ID, Name, Age, Sex, Branch, Address, Occupation, Purpose, Chief, Current)
  patientId: '', // This will be auto-generated
  name: '',
  age: '',
  sex: '',
  branch: '', // New field for branch
  address: '',
  phone: '',
  occupation: '',
  purposeOfVisit: '',
  chiefComplaint: '',
  currentInvestigation: '',
  // History (ordered: General, Past Ocular, IOP, Family, Allergy, Medications)
  generalHistory: '',
  pastOcularHistory: '',
  iop: { od: '', os: '' },
  familyHistory: '',
  allergy: '',
  medications: '',
  // Old Prescription (nested OD, OS, Add)
  oldPx: {
    od: { sph: '', cyl: '', a: '', bcva: '' },
    os: { sph: '', cyl: '', a: '', bcva: '' },
    add: ''
  },
  // Auto Refraction (AR) (nested OD, OS)
  ar: {
    od: { sph: '', cyl: '', a: '', pd: '' },
    os: { sph: '', cyl: '', a: '', pd: '' }
  },
  // Acceptance (nested OD, OS, Add)
  acceptance: {
    od: { ucva: '', sph: '', cyl: '', a: '', bcva: '', duoChrome: '', jcc: '' },
    os: { ucva: '', sph: '', cyl: '', a: '', bcva: '', duoChrome: '', jcc: '' },
    add: ''
  },
  // Examination - Anterior Segment (ordered by table rows: Lids, Conjunctiva, etc.)
  anteriorSegment: {
    lids: { od: '', os: '' },
    conjunctiva: { od: '', os: '' },
    sclera: { od: '', os: '' },
    cornea: { od: '', os: '' },
    anteriorChamber: { od: '', os: '' },
    iris: { od: '', os: '' },
    pupil: { od: '', os: '' },
    lens: { od: '', os: '' },
    anteriorVitreous: { od: '', os: '' }
  },
  // Examination - Posterior Segment (ordered by table rows: Disc, Macula, etc.)
  posteriorSegment: {
    disc: { od: '', os: '' },
    macula: { od: '', os: '' },
    vitreous: { od: '', os: '' },
    retina: { od: '', os: '' },
    fovealReflex: { od: '', os: '' },
    bloodVessels: { od: '', os: '' },
    periphery: { od: '', os: '' }
  },
  // Other (ordered: Other Tests, Diagnosis, Remarks)
  otherTestsAndEvaluations: '',
  diagnosis: '',
  remarks: ''
};

const PatientDetails = () => {
  const navigate = useNavigate();
  const basicsKeys = ['patientId', 'name', 'age', 'sex', 'branch', 'address', 'phone', 'occupation'];
  const [formData, setFormData] = useState(initialFormData);
  const [mode, setMode] = useState('old'); // Start directly in 'old' mode
  const [isNewVisit, setIsNewVisit] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState(null);
  const [currentVisitDate, setCurrentVisitDate] = useState(null);
  const [currentVisitIndex, setCurrentVisitIndex] = useState(-1);
  const [patientBasics, setPatientBasics] = useState({});
  const [patientVisits, setPatientVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState(-1);

  const [userBranch, setUserBranch] = useState(null);

  // Fetch branch on component mount
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const username = localStorage.getItem('currentUsername');
        if (!username) {
          setFormData(prev => ({ ...prev, branch: 'ADMIN' }));
          setUserBranch('ADMIN');
          return;
        }
        // First, try LDB (as referenced)
        const ldbDoc = await getDoc(doc(db, 'LDB', username));
        if (ldbDoc.exists()) {
          const ldbData = ldbDoc.data();
          if (ldbData.branch) {
            setFormData(prev => ({ ...prev, branch: ldbData.branch }));
            setUserBranch(ldbData.branch);
            return;
          }
        }
        // If no branch in LDB, fallback to LUB
        const lubDoc = await getDoc(doc(db, 'LUB', username));
        if (lubDoc.exists()) {
          const lubData = lubDoc.data();
          if (lubData.branch) {
            setFormData(prev => ({ ...prev, branch: lubData.branch }));
            setUserBranch(lubData.branch);
            return;
          }
        }
        // If still no branch, set to ADMIN
        setFormData(prev => ({ ...prev, branch: 'ADMIN' }));
        setUserBranch('ADMIN');
      } catch (error) {
        console.error('Error fetching branch:', error);
        setFormData(prev => ({ ...prev, branch: 'ADMIN' }));
        setUserBranch('ADMIN');
      }
    };
    fetchBranch();
  }, []);

  // Fetch patients when mode changes to 'old' or userBranch changes
  useEffect(() => {
    const fetchPatients = async () => {
      if (userBranch === null) return;

      try {
        let q;
        if (userBranch === 'ADMIN') {
          q = query(collection(db, 'PatientDetail'), orderBy('createdAt', 'desc'));
        } else {
          // Avoid composite index error by sorting in memory
          q = query(collection(db, 'PatientDetail'), where('branch', '==', userBranch));
        }

        const querySnapshot = await getDocs(q);
        const pats = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const latestVisit = data.visits && data.visits.length > 0 ? data.visits[data.visits.length - 1].date.toDate().toLocaleDateString() : 'No visits';
          pats.push({
            id: docSnap.id,
            ...data,
            latestVisit
          });
        });

        if (userBranch !== 'ADMIN') {
          pats.sort((a, b) => {
            const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
            return dateB - dateA;
          });
        }

        setPatients(pats);
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };
    if (mode === 'old') {
      fetchPatients();
    }
  }, [mode, userBranch]);

  // Handle input changes for flat fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle select changes (for sex)
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle nested object changes (e.g., for OD/OS fields in deeper structures)
  const handleNestedChange = (section, side, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [side]: {
          ...prev[section][side],
          [field]: value
        }
      }
    }));
  };

  // Specific handler for IOP (depth 2 nesting, ordered OD then OS)
  const handleIopChange = (side, value) => {
    setFormData(prev => ({
      ...prev,
      iop: {
        ...prev.iop,
        [side]: value
      }
    }));
  };

  // Get next auto-incrementing Patient ID (format: 26001, 26002, etc.)
  const getNextPatientId = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'PatientDetail'));
      const currentYear = new Date().getFullYear().toString().slice(-2); // Get last 2 digits of year
      const yearPrefix = currentYear; // e.g., "26" for 2026

      let maxOrderNum = 0;
      querySnapshot.forEach((docSnap) => {
        const patientId = docSnap.id;
        // Check if ID starts with current year prefix
        if (patientId.startsWith(yearPrefix)) {
          const orderNum = parseInt(patientId.slice(2)); // Get the last 3 digits
          if (!isNaN(orderNum) && orderNum > maxOrderNum) {
            maxOrderNum = orderNum;
          }
        }
      });

      const nextOrderNum = maxOrderNum + 1;
      // Format: year (2 digits) + order number (3 digits with leading zeros)
      return yearPrefix + nextOrderNum.toString().padStart(3, '0');
    } catch (error) {
      console.error('Error getting next Patient ID:', error);
      const currentYear = new Date().getFullYear().toString().slice(-2);
      return currentYear + '001'; // Fallback to year + 001 if error
    }
  };

  // Load patient for list or new visit
  const loadPatientForView = async (id, forNewVisit = false) => {
    try {
      const docSnap = await getDoc(doc(db, 'PatientDetail', id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        const basics = {
          patientId: data.patientId || data.sNo || '', // Support old sNo field for backward compatibility
          name: data.name || '',
          age: data.age || '',
          sex: data.sex || '',
          branch: data.branch || '',
          address: data.address || '',
          phone: data.phone || data.mobile || data.Mobile || '',
          occupation: data.occupation || '',
        };
        const visits = data.visits || [];
        setPatientBasics(basics);
        setPatientVisits(visits);
        setCurrentPatientId(id);
        if (forNewVisit) {
          // Load empty for new visit
          let visitDetails = {};
          Object.keys(initialFormData).forEach(key => {
            if (!basicsKeys.includes(key)) {
              if (typeof initialFormData[key] === 'object' && initialFormData[key] !== null) {
                visitDetails[key] = JSON.parse(JSON.stringify(initialFormData[key]));
              } else {
                visitDetails[key] = '';
              }
            }
          });
          setFormData({ ...basics, ...visitDetails });
          setCurrentVisitDate(null);
          setIsNewVisit(true);
          setCurrentVisitIndex(-1);
          setMode('visitEdit');
        } else {
          // Load patient list view
          setMode('patientList');
        }
      }
    } catch (error) {
      console.error('Error loading patient:', error);
      alert('Error loading patient data.');
    }
  };

  // Edit a specific visit
  const editVisit = (index) => {
    const originalVisit = patientVisits[index];
    const visitDetails = JSON.parse(JSON.stringify(originalVisit));
    delete visitDetails.date; // Exclude date to avoid stringifying it into formData/saves
    setFormData({ ...patientBasics, ...visitDetails });
    setCurrentVisitDate(originalVisit.date); // Use original Timestamp
    setIsNewVisit(false);
    setCurrentVisitIndex(index);
    setMode('visitEdit');
  };

  // Add new visit
  const addNewVisit = () => {
    let visitDetails = {};
    Object.keys(initialFormData).forEach(key => {
      if (!basicsKeys.includes(key)) {
        if (typeof initialFormData[key] === 'object' && initialFormData[key] !== null) {
          visitDetails[key] = JSON.parse(JSON.stringify(initialFormData[key]));
        } else {
          visitDetails[key] = '';
        }
      }
    });
    setFormData({ ...patientBasics, ...visitDetails });
    setIsNewVisit(true);
    setCurrentVisitIndex(-1);
    setCurrentVisitDate(null);
    setMode('visitEdit');
  };

  // Show confirmation modal for delete
  const confirmDelete = (index) => {
    setVisitToDelete(index);
    setShowConfirm(true);
  };

  // Handle confirmed delete
  const handleConfirmedDelete = async () => {
    if (visitToDelete === -1 || !currentPatientId) {
      setShowConfirm(false);
      setVisitToDelete(-1);
      return;
    }
    try {
      const docRef = doc(db, 'PatientDetail', currentPatientId);
      const newVisits = patientVisits.filter((_, i) => i !== visitToDelete);
      await setDoc(docRef, { visits: newVisits }, { merge: true });
      setPatientVisits(newVisits);
      alert('Visit deleted successfully!');
    } catch (error) {
      console.error('Error deleting visit:', error);
      alert('Error deleting visit. Please try again.');
    }
    setShowConfirm(false);
    setVisitToDelete(-1);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowConfirm(false);
    setVisitToDelete(-1);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.age || !formData.sex) {
      alert('Please fill in the required fields: Name, Age, and Sex.');
      return;
    }
    if (mode === 'newPatient' && !formData.patientId) {
      alert('Patient ID is missing. Please try again.');
      return;
    }
    try {
      const basics = {};
      const visitDetails = {};
      Object.keys(formData).forEach(key => {
        if (basicsKeys.includes(key)) {
          basics[key] = formData[key];
        } else {
          visitDetails[key] = formData[key];
        }
      });
      if (mode === 'newPatient') {
        // Check if Patient ID already exists
        const existingDoc = await getDoc(doc(db, 'PatientDetail', formData.patientId));
        if (existingDoc.exists()) {
          alert('A patient with this Patient ID already exists. Please try again.');
          return;
        }
        const patientData = {
          ...basics,
          createdAt: new Date(),
          visits: [{
            date: Timestamp.fromDate(new Date()),
            ...visitDetails
          }]
        };
        await setDoc(doc(db, 'PatientDetail', formData.patientId), patientData);
        alert('New patient added successfully!');
        setFormData({ ...initialFormData, branch: userBranch });
        setMode('old'); // Go back to old patients list
      } else if (mode === 'visitEdit') {
        if (!currentPatientId) {
          alert('No patient selected.');
          return;
        }
        const docRef = doc(db, 'PatientDetail', currentPatientId);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          alert('Patient not found.');
          return;
        }
        const data = docSnap.data();
        let newVisits;
        if (isNewVisit) {
          const newVisit = {
            date: Timestamp.fromDate(new Date()),
            ...visitDetails
          };
          newVisits = [...(data.visits || []), newVisit];
          alert('New visit added successfully!');
        } else {
          newVisits = (data.visits || []).map((v, i) => {
            if (i === currentVisitIndex) {
              return { ...v, ...visitDetails };
            }
            return v;
          });
          alert('Visit updated successfully!');
        }
        await setDoc(docRef, { visits: newVisits }, { merge: true });
        setPatientVisits(newVisits);
        setMode('patientList');
        setCurrentPatientId(null);
        setIsNewVisit(false);
        setCurrentVisitDate(null);
        setCurrentVisitIndex(-1);
        setFormData({ ...initialFormData, branch: userBranch });
      }
    } catch (error) {
      console.error('Error saving patient details:', error);
      alert('Error saving data. Please try again.');
    }
  };

  const [activeTab, setActiveTab] = useState(0);

  const filteredPatients = patients.filter(p => {
    const term = searchTerm.toLowerCase();
    const nameMatch = p.name && p.name.toLowerCase().includes(term);
    const phoneMatch = p.phone && p.phone.toLowerCase().includes(term);
    const mobileMatch = p.mobile && p.mobile.toLowerCase().includes(term);
    const MobileMatch = p.Mobile && p.Mobile.toLowerCase().includes(term);
    return nameMatch || phoneMatch || mobileMatch || MobileMatch;
  });

  const sections = [
    {
      id: 0,
      title: 'Basic Information',
      content: (
        <>
          <label className="block mb-2 text-black">Name: <input type="text" name="name" value={formData.name} onChange={handleChange} readOnly={mode !== 'newPatient'} required className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" /></label>
          <label className="block mb-2 text-black">Age: <input type="number" name="age" value={formData.age} onChange={handleChange} readOnly={mode !== 'newPatient'} required min="0" className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" /></label>
          <label className="block mb-2 text-black">Sex:
            <select name="sex" value={formData.sex} onChange={handleSelectChange} disabled={mode !== 'newPatient'} required className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none">
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className="block mb-2 text-black">Patient ID: <input type="text" name="patientId" value={formData.patientId} readOnly placeholder="Auto-generated" className="w-full p-2 rounded bg-gray-200 text-black border border-black/20 cursor-not-allowed" /></label>
          <label className="block mb-2 text-black">Branch: <input type="text" name="branch" value={formData.branch} readOnly placeholder="Auto-filled" className="w-full p-2 rounded bg-gray-100 text-black border border-black/20" /></label>
          <label className="block mb-2 text-black">Address: <textarea name="address" value={formData.address} onChange={handleChange} readOnly={mode !== 'newPatient'} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Phone: <input type="tel" name="phone" value={formData.phone} onChange={handleChange} readOnly={mode !== 'newPatient'} placeholder="Enter phone number" className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" /></label>
          <label className="block mb-2 text-black">Occupation: <input type="text" name="occupation" value={formData.occupation} onChange={handleChange} readOnly={mode !== 'newPatient'} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" /></label>
          <label className="block mb-2 text-black">Purpose of Visit: <textarea name="purposeOfVisit" value={formData.purposeOfVisit} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Chief Complaint: <textarea name="chiefComplaint" value={formData.chiefComplaint} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Current Investigation: <textarea name="currentInvestigation" value={formData.currentInvestigation} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
        </>
      )
    },
    {
      id: 1,
      title: 'History',
      content: (
        <>
          <label className="block mb-2 text-black">General History: <textarea name="generalHistory" value={formData.generalHistory} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Past Ocular History: <textarea name="pastOcularHistory" value={formData.pastOcularHistory} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <div className="flex gap-4 mb-2">
            <label className="flex-1 text-black">IOP OD: <input type="text" value={formData.iop.od} onChange={(e) => handleIopChange('od', e.target.value)} placeholder="OD" className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none mt-1" /></label>
            <label className="flex-1 text-black">IOP OS: <input type="text" value={formData.iop.os} onChange={(e) => handleIopChange('os', e.target.value)} placeholder="OS" className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none mt-1" /></label>
          </div>
          <label className="block mb-2 text-black">Family History: <textarea name="familyHistory" value={formData.familyHistory} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Allergy: <textarea name="allergy" value={formData.allergy} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
          <label className="block mb-2 text-black">Medications: <textarea name="medications" value={formData.medications} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
        </>
      )
    },
    {
      id: 2,
      title: 'Old Prescription',
      content: (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black/20 text-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black/20 p-1 text-xs"> </th>
                <th className="border border-black/20 p-1 text-xs">Sph</th>
                <th className="border border-black/20 p-1 text-xs">Cyl</th>
                <th className="border border-black/20 p-1 text-xs">A</th>
                <th className="border border-black/20 p-1 text-xs">BCVA</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OD</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.od.sph} onChange={(e) => handleNestedChange('oldPx', 'od', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.od.cyl} onChange={(e) => handleNestedChange('oldPx', 'od', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.od.a} onChange={(e) => handleNestedChange('oldPx', 'od', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.od.bcva} onChange={(e) => handleNestedChange('oldPx', 'od', 'bcva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OS</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.os.sph} onChange={(e) => handleNestedChange('oldPx', 'os', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.os.cyl} onChange={(e) => handleNestedChange('oldPx', 'os', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.os.a} onChange={(e) => handleNestedChange('oldPx', 'os', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.oldPx.os.bcva} onChange={(e) => handleNestedChange('oldPx', 'os', 'bcva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Add</td>
                <td colSpan="4" className="border border-black/20 p-1"><input type="text" value={formData.oldPx.add} onChange={(e) => setFormData(prev => ({ ...prev, oldPx: { ...prev.oldPx, add: e.target.value } }))} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 3,
      title: 'Auto Refraction (AR)',
      content: (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black/20 text-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black/20 p-1 text-xs"> </th>
                <th className="border border-black/20 p-1 text-xs">Sph</th>
                <th className="border border-black/20 p-1 text-xs">Cyl</th>
                <th className="border border-black/20 p-1 text-xs">A</th>
                <th className="border border-black/20 p-1 text-xs">PD</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OD</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.od.sph} onChange={(e) => handleNestedChange('ar', 'od', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.od.cyl} onChange={(e) => handleNestedChange('ar', 'od', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.od.a} onChange={(e) => handleNestedChange('ar', 'od', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.od.pd} onChange={(e) => handleNestedChange('ar', 'od', 'pd', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OS</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.os.sph} onChange={(e) => handleNestedChange('ar', 'os', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.os.cyl} onChange={(e) => handleNestedChange('ar', 'os', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.os.a} onChange={(e) => handleNestedChange('ar', 'os', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.ar.os.pd} onChange={(e) => handleNestedChange('ar', 'os', 'pd', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 4,
      title: 'Acceptance',
      content: (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black/20 text-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black/20 p-1 text-xs"> </th>
                <th className="border border-black/20 p-1 text-xs">UCVA</th>
                <th className="border border-black/20 p-1 text-xs">Sph</th>
                <th className="border border-black/20 p-1 text-xs">Cyl</th>
                <th className="border border-black/20 p-1 text-xs">A</th>
                <th className="border border-black/20 p-1 text-xs">BCVA</th>
                <th className="border border-black/20 p-1 text-xs">Duo-chrome</th>
                <th className="border border-black/20 p-1 text-xs">Jcc</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OD</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.ucva} onChange={(e) => handleNestedChange('acceptance', 'od', 'ucva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.sph} onChange={(e) => handleNestedChange('acceptance', 'od', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.cyl} onChange={(e) => handleNestedChange('acceptance', 'od', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.a} onChange={(e) => handleNestedChange('acceptance', 'od', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.bcva} onChange={(e) => handleNestedChange('acceptance', 'od', 'bcva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.duoChrome} onChange={(e) => handleNestedChange('acceptance', 'od', 'duoChrome', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.od.jcc} onChange={(e) => handleNestedChange('acceptance', 'od', 'jcc', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">OS</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.ucva} onChange={(e) => handleNestedChange('acceptance', 'os', 'ucva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.sph} onChange={(e) => handleNestedChange('acceptance', 'os', 'sph', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.cyl} onChange={(e) => handleNestedChange('acceptance', 'os', 'cyl', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.a} onChange={(e) => handleNestedChange('acceptance', 'os', 'a', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.bcva} onChange={(e) => handleNestedChange('acceptance', 'os', 'bcva', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.duoChrome} onChange={(e) => handleNestedChange('acceptance', 'os', 'duoChrome', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.acceptance.os.jcc} onChange={(e) => handleNestedChange('acceptance', 'os', 'jcc', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Add</td>
                <td colSpan="7" className="border border-black/20 p-1"><input type="text" value={formData.acceptance.add} onChange={(e) => setFormData(prev => ({ ...prev, acceptance: { ...prev.acceptance, add: e.target.value } }))} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 5,
      title: 'Anterior Segment Examination',
      content: (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black/20 text-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black/20 p-1 text-xs">Examination</th>
                <th className="border border-black/20 p-1 text-xs">OD</th>
                <th className="border border-black/20 p-1 text-xs">OS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Lids</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.lids.od} onChange={(e) => handleNestedChange('anteriorSegment', 'lids', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.lids.os} onChange={(e) => handleNestedChange('anteriorSegment', 'lids', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Conjunctiva</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.conjunctiva.od} onChange={(e) => handleNestedChange('anteriorSegment', 'conjunctiva', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.conjunctiva.os} onChange={(e) => handleNestedChange('anteriorSegment', 'conjunctiva', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Sclera</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.sclera.od} onChange={(e) => handleNestedChange('anteriorSegment', 'sclera', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.sclera.os} onChange={(e) => handleNestedChange('anteriorSegment', 'sclera', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Cornea</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.cornea.od} onChange={(e) => handleNestedChange('anteriorSegment', 'cornea', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.cornea.os} onChange={(e) => handleNestedChange('anteriorSegment', 'cornea', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Anterior Chamber</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.anteriorChamber.od} onChange={(e) => handleNestedChange('anteriorSegment', 'anteriorChamber', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.anteriorChamber.os} onChange={(e) => handleNestedChange('anteriorSegment', 'anteriorChamber', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Iris</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.iris.od} onChange={(e) => handleNestedChange('anteriorSegment', 'iris', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.iris.os} onChange={(e) => handleNestedChange('anteriorSegment', 'iris', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Pupil</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.pupil.od} onChange={(e) => handleNestedChange('anteriorSegment', 'pupil', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.pupil.os} onChange={(e) => handleNestedChange('anteriorSegment', 'pupil', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Lens</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.lens.od} onChange={(e) => handleNestedChange('anteriorSegment', 'lens', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.lens.os} onChange={(e) => handleNestedChange('anteriorSegment', 'lens', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Anterior Vitreous</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.anteriorVitreous.od} onChange={(e) => handleNestedChange('anteriorSegment', 'anteriorVitreous', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.anteriorSegment.anteriorVitreous.os} onChange={(e) => handleNestedChange('anteriorSegment', 'anteriorVitreous', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 6,
      title: 'Posterior Segment Examination',
      content: (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-black/20 text-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black/20 p-1 text-xs">Examination</th>
                <th className="border border-black/20 p-1 text-xs">OD</th>
                <th className="border border-black/20 p-1 text-xs">OS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Disc</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.disc.od} onChange={(e) => handleNestedChange('posteriorSegment', 'disc', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.disc.os} onChange={(e) => handleNestedChange('posteriorSegment', 'disc', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Macula</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.macula.od} onChange={(e) => handleNestedChange('posteriorSegment', 'macula', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.macula.os} onChange={(e) => handleNestedChange('posteriorSegment', 'macula', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Vitreous</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.vitreous.od} onChange={(e) => handleNestedChange('posteriorSegment', 'vitreous', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.vitreous.os} onChange={(e) => handleNestedChange('posteriorSegment', 'vitreous', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Retina</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.retina.od} onChange={(e) => handleNestedChange('posteriorSegment', 'retina', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.retina.os} onChange={(e) => handleNestedChange('posteriorSegment', 'retina', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Foveal Reflex</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.fovealReflex.od} onChange={(e) => handleNestedChange('posteriorSegment', 'fovealReflex', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.fovealReflex.os} onChange={(e) => handleNestedChange('posteriorSegment', 'fovealReflex', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Blood Vessels</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.bloodVessels.od} onChange={(e) => handleNestedChange('posteriorSegment', 'bloodVessels', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.bloodVessels.os} onChange={(e) => handleNestedChange('posteriorSegment', 'bloodVessels', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
              <tr>
                <td className="border border-black/20 p-1 text-xs font-semibold">Periphery</td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.periphery.od} onChange={(e) => handleNestedChange('posteriorSegment', 'periphery', 'od', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
                <td className="border border-black/20 p-1"><input type="text" value={formData.posteriorSegment.periphery.os} onChange={(e) => handleNestedChange('posteriorSegment', 'periphery', 'os', e.target.value)} className="w-full bg-transparent text-black focus:outline-none border border-black/20 focus:border-black text-xs p-1" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 7,
      title: 'Other Tests and Evaluations',
      content: (
        <label className="block text-black"><textarea name="otherTestsAndEvaluations" value={formData.otherTestsAndEvaluations} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="4" /></label>
      )
    },
    {
      id: 8,
      title: 'Diagnosis',
      content: (
        <label className="block text-black"><textarea name="diagnosis" value={formData.diagnosis} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
      )
    },
    {
      id: 9,
      title: 'Remarks',
      content: (
        <label className="block text-black"><textarea name="remarks" value={formData.remarks} onChange={handleChange} className="w-full p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none" rows="3" /></label>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
      {/* Header */}
      <h1 className="text-2xl sm:text-3xl font-extrabold text-black mb-4 relative text-center">
        Aram Optometrist Clinic - Patient Details
        <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/4 h-1 bg-black rounded-full" />
      </h1>
      {/* Back Bar */}
      <div
        className="w-full max-w-md mb-4 p-3 bg-black text-white rounded-xl shadow-md flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-800 text-sm"
        onClick={() => navigate('/StoreManagement')}
      >
        <FaArrowLeft className="text-sm" />
        <span className="font-semibold">Back to Store Management</span>
      </div>
      {/* Main Content */}
      <div className="max-w-5xl w-full">
        {mode === 'old' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <button
                onClick={async () => {
                  const nextId = await getNextPatientId();
                  setFormData({ ...initialFormData, patientId: nextId, branch: userBranch });
                  setMode('newPatient');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800"
              >
                <FaUserPlus />
                <span>New Patient</span>
              </button>
              <input
                type="text"
                placeholder="Search by name and by phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 p-2 rounded bg-gray-100 text-black border border-black/20 focus:border-black focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto p-2">
              {filteredPatients.length === 0 ? (
                <div className="col-span-full text-center py-8 bg-gray-50 rounded-xl border border-black/10">
                  <p className="text-gray-500 font-medium">No patients found.</p>
                </div>
              ) : (
                filteredPatients.map((p) => (
                  <div key={p.id} className="flex flex-col p-4 bg-white rounded-xl border border-black/10 shadow-sm hover:shadow-md transition-shadow">
                    <div
                      onClick={() => loadPatientForView(p.id, false)}
                      className="cursor-pointer flex-1"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-lg text-black truncate pr-2">{p.name}</h3>
                        <span className="text-xs font-semibold bg-gray-200 text-gray-800 px-2 py-1 rounded-full whitespace-nowrap">ID: {p.patientId || p.sNo}</span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                        <span><strong className="text-gray-800">Phone:</strong> {p.phone || p.mobile || p.Mobile || 'N/A'}</span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                        <span><strong className="text-gray-800">Age/Sex:</strong> {p.age} / {p.sex}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                        <strong className="text-gray-700">Latest Visit:</strong> {p.latestVisit}
                      </div>
                    </div>
                    <button
                      onClick={() => loadPatientForView(p.id, true)}
                      className="mt-3 w-full py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                      title="Add New Visit"
                    >
                      <FaPlus /> Add Visit
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {mode === 'patientList' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-black mb-4 text-center">
              Patient: {patientBasics.name} - All Visits
            </h2>
            <button
              type="button"
              onClick={() => {
                setMode('old');
                setCurrentPatientId(null);
              }}
              className="mb-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Back to Patients
            </button>
            {/* Main Patient Data (Basics) - Read-only */}
            <div className="p-4 rounded-xl bg-white text-black shadow-md border border-black/20">
              <h3 className="text-lg font-semibold mb-3 border-b border-black/20 pb-1">Patient Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-black"><strong>Patient ID:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.patientId || patientBasics.sNo}</span></label>
                <label className="block text-black"><strong>Name:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.name}</span></label>
                <label className="block text-black"><strong>Age:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.age}</span></label>
                <label className="block text-black"><strong>Sex:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.sex}</span></label>
                <label className="block text-black"><strong>Branch:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.branch}</span></label>
                <label className="block text-black"><strong>Address:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.address}</span></label>
                <label className="block text-black"><strong>Phone:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.phone}</span></label>
                <label className="block text-black"><strong>Occupation:</strong> <span className="bg-gray-100 p-2 rounded">{patientBasics.occupation}</span></label>
              </div>
            </div>
            {/* Visits List */}
            <div className="p-4 rounded-xl bg-white text-black shadow-md border border-black/20">
              <h3 className="text-lg font-semibold mb-3 border-b border-black/20 pb-1">Visits History</h3>
              <div className="space-y-2">
                {patientVisits.length === 0 ? (
                  <p className="text-center text-gray-500">No visits yet. Add one below!</p>
                ) : (
                  patientVisits.map((visit, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded border border-black/20">
                      <span className="font-semibold">Visit on {visit.date.toDate().toLocaleDateString()}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => editVisit(index)}
                          className="p-1 bg-black text-white rounded hover:bg-gray-800 flex items-center gap-1 text-sm"
                          title="Edit Visit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => confirmDelete(index)}
                          className="p-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-1 text-sm"
                          title="Delete Visit"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={addNewVisit}
              className="w-full px-6 py-2 bg-black text-white rounded-xl shadow-md font-semibold cursor-pointer hover:bg-gray-800 transition-colors"
            >
              Add New Visit
            </button>
          </div>
        )}
        {(mode === 'newPatient' || mode === 'visitEdit') && (
          <div>
            {mode === 'visitEdit' && (
              <h2 className="text-xl font-semibold text-black mb-4 text-center">
                Patient: {formData.name} {isNewVisit ? '- New Visit' : `- Edit Visit on ${currentVisitDate?.toDate().toLocaleDateString()}`}
              </h2>
            )}
            <button
              type="button"
              onClick={() => {
                if (mode === 'newPatient') {
                  setMode('old');
                } else {
                  setMode('patientList');
                }
              }}
              className="mb-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Back
            </button>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tabs Navigation */}
              <div className="flex overflow-x-auto hide-scrollbar border-b border-black/20 pb-2 mb-4 gap-2">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveTab(index)}
                    className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === index
                        ? 'bg-black text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                      }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>

              {/* Active Tab Content */}
              <div className="p-6 rounded-xl bg-white text-black shadow-lg border border-black/10 min-h-[400px] flex flex-col justify-between">
                <div>
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-black/10 text-black">
                    {sections[activeTab].title}
                  </h2>
                  <div className="relative z-10 animate-fadeIn">
                    {sections[activeTab].content}
                  </div>
                </div>

                {/* Navigation Buttons inside Form */}
                <div className="mt-8 flex justify-between items-center pt-4 border-t border-black/10">
                  <button
                    type="button"
                    onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
                    disabled={activeTab === 0}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-black hover:bg-gray-300'
                      }`}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(prev => Math.min(sections.length - 1, prev + 1))}
                    disabled={activeTab === sections.length - 1}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === sections.length - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-black hover:bg-gray-300'
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full mt-6 px-6 py-3 bg-black text-white rounded-xl shadow-md font-semibold cursor-pointer hover:bg-gray-800 transition-colors text-lg"
              >
                {mode === 'newPatient' ? 'Save New Patient' : (isNewVisit ? 'Save New Visit' : 'Update Visit')}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-black">Confirm Delete</h3>
            <p className="mb-4 text-black">Are you sure you want to delete this visit? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;